/**
 * Voice KPI-row metrics for the main dashboard (GET /v1/voice/stats/overview).
 *
 * Separate hook file from useStats.ts (SMS) mirroring the voice-isolation
 * split on the backend. The endpoint is 404 unless cfg.VoiceEnabled at the
 * API, so callers must only enable this behind the same useFeature('voice')
 * gate the rest of /voice/* uses — pass `enabled` accordingly.
 */

import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface VoiceStatsVolumePoint {
  /** ISO date (YYYY-MM-DD) */
  day: string;
  calls: number;
  transfers: number;
}

export interface VoiceStatsOverview {
  calls_today: number;
  /** Fractional change vs yesterday; null when yesterday had no calls. */
  calls_today_delta: number | null;
  /** Answered / dialed over the last 7 days; null when no calls dialed. */
  answer_rate: number | null;
  transfers_today: number;
  transfers_today_delta: number | null;
  call_volume_7d: number;
  /** Week-over-week change in call volume; null when the prior week was 0. */
  call_volume_delta: number | null;
  /** Transfers / dialed over the last 7 days; null when no calls dialed. */
  transfer_rate_7d: number | null;
  /** 14-day daily call + transfer series for the stacked chart. */
  volume: VoiceStatsVolumePoint[];
}

const VOICE_STATS_KEY = ['voice', 'stats', 'overview'] as const;

interface Options {
  enabled?: boolean;
}

export function useVoiceStatsOverview({ enabled = true }: Options = {}) {
  return useQuery({
    queryKey: VOICE_STATS_KEY,
    queryFn: () => api.get<VoiceStatsOverview>('/v1/voice/stats/overview'),
    enabled,
    refetchInterval: enabled ? 30_000 : false,
    refetchIntervalInBackground: false,
    staleTime: 15_000,
  });
}
